import React, { useState, useMemo } from "react";
import { useData } from "@/context/DataContext";
import { PageTitle, Fab, StickyTotal, RowActions, EmptyState } from "@/components/shared";
import DataDialog from "@/components/DataDialog";
import { formatRupiah, parseNumber, formatDate } from "@/lib/format";
import { payableOptions } from "@/lib/compute";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export default function Pembayaran() {
  const { data, create, update, remove } = useData();
  const items = data.pembayaran || [];
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const totalPaid = items.reduce((s, i) => s + parseNumber(i.nominal), 0);
  const sorted = [...items].sort((a, b) => (b.tanggal || "").localeCompare(a.tanggal || ""));

  const options = useMemo(() => {
    const base = payableOptions(data).map((o) => ({ value: `${o.type}::${o.id}`, label: o.label }));
    // include current source when editing (in case it became fully paid)
    if (editing && editing.source_id) {
      const key = `${editing.source_type}::${editing.source_id}`;
      if (!base.find((b) => b.value === key)) base.unshift({ value: key, label: `${editing.source_name} (dipilih)` });
    }
    return base;
  }, [data, editing]);

  const fields = [
    { name: "source", label: "Pembayaran Untuk", type: "select", options, required: true },
    { name: "tanggal", label: "Tanggal Bayar", type: "date", required: true },
    { name: "notes", label: "Notes", type: "textarea" },
    { name: "nominal", label: "Nominal Bayar", type: "currency", required: true },
  ];

  const onSave = async (payload) => {
    const [source_type, source_id] = String(payload.source).split("::");
    const all = payableOptions(data);
    let name = all.find((o) => o.id === source_id)?.name;
    if (!name && editing) name = editing.source_name;
    const doc = { source_type, source_id, source_name: name || "", tanggal: payload.tanggal, notes: payload.notes, nominal: payload.nominal };
    if (editing) { await update("pembayaran", editing.id, doc); toast.success("Pembayaran diperbarui"); }
    else { await create("pembayaran", doc); toast.success("Pembayaran ditambahkan"); }
  };

  const openEdit = (i) => { setEditing({ ...i, source: `${i.source_type}::${i.source_id}` }); setOpen(true); };

  return (
    <div>
      <PageTitle title="Pembayaran" subtitle="Bayar item budget, seserahan & life after married" />
      <StickyTotal testId="pembayaran-total">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Sudah Dibayarkan</p>
        <p className="font-heading text-3xl font-semibold" data-testid="pembayaran-total-value">{formatRupiah(totalPaid)}</p>
      </StickyTotal>
      <div className="rounded-xl border border-border">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Tanggal</TableHead><TableHead>Untuk</TableHead><TableHead>Notes</TableHead>
            <TableHead className="text-right">Nominal</TableHead><TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {sorted.map((i) => (
              <TableRow key={i.id} data-testid={`row-${i.id}`}>
                <TableCell>{formatDate(i.tanggal)}</TableCell>
                <TableCell className="font-medium">{i.source_name} <span className="text-xs text-muted-foreground">({i.source_type})</span></TableCell>
                <TableCell className="max-w-[160px] truncate text-muted-foreground">{i.notes || "-"}</TableCell>
                <TableCell className="text-right">{formatRupiah(i.nominal)}</TableCell>
                <TableCell><RowActions id={i.id} onEdit={() => openEdit(i)} onDelete={() => remove("pembayaran", i.id)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {items.length === 0 && <EmptyState />}
      </div>
      <Fab testId="fab-pembayaran" onClick={() => { setEditing(null); setOpen(true); }} />
      <DataDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Pembayaran" : "Tambah Pembayaran"} fields={fields} initial={editing} onSave={onSave} />
    </div>
  );
}
